/**
 * Injects the current club's custom scripts into document head/body.
 * Scripts from other clubs are never loaded (API is scoped by X-Club-Key).
 */
import { useEffect } from 'react';
import { apiFetch, getClubKey } from '../utils/api';

const MARKER = 'data-club-script';

function removeInjected() {
    document.querySelectorAll(`[${MARKER}]`).forEach((el) => el.remove());
}

function cloneForDom(node) {
    if (node.nodeType === Node.COMMENT_NODE) {
        return document.createComment(node.nodeValue || '');
    }
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (!text.trim()) return null;
        return document.createTextNode(text);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    if (node.tagName === 'SCRIPT') {
        const script = document.createElement('script');
        for (const attr of node.attributes) {
            script.setAttribute(attr.name, attr.value);
        }
        script.text = node.textContent || '';
        script.setAttribute(MARKER, '1');
        return script;
    }

    const el = document.createElement(node.tagName.toLowerCase());
    for (const attr of node.attributes) {
        el.setAttribute(attr.name, attr.value);
    }
    el.setAttribute(MARKER, '1');
    el.innerHTML = node.innerHTML;
    return el;
}

function nodesFromHtml(html) {
    const parsed = new DOMParser().parseFromString(String(html || ''), 'text/html');
    return [...parsed.head.childNodes, ...parsed.body.childNodes]
        .map(cloneForDom)
        .filter(Boolean);
}

function mount(position, nodes) {
    const head = document.head;
    const body = document.body;
    const headRef = head.firstChild;
    const bodyRef = body.firstChild;
    nodes.forEach((el) => {
        if (el.nodeType === Node.ELEMENT_NODE && !el.hasAttribute(MARKER)) {
            el.setAttribute(MARKER, '1');
        }
        if (position === 'head_start') {
            head.insertBefore(el, headRef);
        } else if (position === 'head_end') {
            head.appendChild(el);
        } else if (position === 'body_start') {
            body.insertBefore(el, bodyRef);
        } else {
            body.appendChild(el);
        }
    });
}

export default function ClubScriptInjector({ enabled }) {
    useEffect(() => {
        if (!enabled || !getClubKey()) {
            removeInjected();
            return undefined;
        }

        let cancelled = false;

        (async () => {
            try {
                const res = await apiFetch('/api/club/scripts');
                if (!res.ok || cancelled) return;
                const scripts = await res.json();
                if (cancelled) return;
                removeInjected();
                for (const script of scripts) {
                    if (script.is_active === false || script.is_active === 0) continue;
                    mount(script.position, nodesFromHtml(script.script_content));
                }
            } catch (err) {
                console.error('[ClubScripts] inject failed:', err);
            }
        })();

        return () => {
            cancelled = true;
            removeInjected();
        };
    }, [enabled]);

    return null;
}
